import React, { useEffect, useState } from "react";
import { CalendarX, Trash2, AlertTriangle } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { toast } from "./Toast.jsx";
import ConfirmDialog from "./ConfirmDialog.jsx";
import UkonceniVolby, { PRAZDNE_UKONCENI, chybaUkonceni } from "./UkonceniVolby.jsx";

// FW-145/155 (15. 10. 2026, schválil Láďa): plánované ukončení spolupráce k datu.
// CSM ho nastaví v kartě Spolupráce, v ten den denní cron (scheduled-planovane-zmeny-baliku)
// změní stav na Ukončeno a Konec na toto datum. 7 dní předem a v den ukončení jde
// do #operations-company připomínka CSM kvůli fakturaci ve FAPI.
// Po ukončení má klientka 90 dní na vychození zbývajících tréninků (doběh), karta ho ukazuje.

const DOBEH_DNI = 90;

function fmtDay(ds) {
  if (!ds) return "";
  const [y, m, d] = String(ds).substring(0, 10).split("-").map(Number);
  return `${d}. ${m}. ${y}`;
}
function dnesPraha() {
  return new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Prague" });
}
function pridejDny(iso, n) {
  const d = new Date(`${String(iso).substring(0, 10)}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().substring(0, 10);
}
function rozdilDni(od, doDne) {
  return Math.round((Date.parse(`${doDne}T12:00:00Z`) - Date.parse(`${od}T12:00:00Z`)) / 86400000);
}

export default function PlanovaneUkonceniSection({ klientId, notionPageId, klientName, stav, konec, stavTreninku, readOnly = false }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [datum, setDatum] = useState("");
  const [potvrdit, setPotvrdit] = useState(false);
  const [zrusit, setZrusit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [menit, setMenit] = useState(false);
  const [ukonceni, setUkonceni] = useState(PRAZDNE_UKONCENI);
  const dnes = dnesPraha();
  const zitra = pridejDny(dnes, 1);

  const idParam = klientId
    ? `klient_id=${encodeURIComponent(klientId)}`
    : (notionPageId ? `notion_page_id=${encodeURIComponent(notionPageId)}` : "");

  const load = async () => {
    if (!idParam) { setLoading(false); return; }
    try {
      const r = await apiFetch(`/api/klient-planovana-zmena?${idParam}`);
      const j = await r.json();
      const arr = (Array.isArray(j.zmeny) ? j.zmeny : []).filter((z) => z.akce === "ukonceni");
      setPlan(arr[0] || null);
    } catch (_) { /* ticho */ }
    setLoading(false);
  };
  useEffect(() => { setLoading(true); load(); }, [idParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const naplanovat = async () => {
    setBusy(true);
    try {
      const r = await apiFetch("/api/klient-planovana-zmena", {
        method: "POST",
        body: JSON.stringify({ action: "set_ukonceni", klient_id: klientId || undefined, klient_name: klientName || undefined, notion_page_id: notionPageId, datum, ukonceni }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || "Uložení selhalo");
      toast("Ukončení naplánováno.", "success");
      setDatum("");
      setMenit(false);
      await load();
    } catch (e) { toast(e.message || "Uložení selhalo", "error"); }
    setBusy(false);
  };
  const zrusitPlan = async () => {
    if (!plan) return;
    setBusy(true);
    try {
      const r = await apiFetch("/api/klient-planovana-zmena", { method: "POST", body: JSON.stringify({ action: "cancel", id: plan.id }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || "Zrušení selhalo");
      toast("Plánované ukončení zrušeno.", "success");
      setPlan(null);
    } catch (e) { toast(e.message || "Zrušení selhalo", "error"); }
    setBusy(false);
  };

  if (!notionPageId && !klientId) return null;

  // Doběh po ukončení: 90 dní od Konce na vychození zbývajících tréninků.
  const ukonceno = /^ukon/i.test(String(stav || ""));
  const konecIso = String(konec || "").substring(0, 10);
  const dobehDo = ukonceno && /^\d{4}-\d{2}-\d{2}$/.test(konecIso) ? pridejDny(konecIso, DOBEH_DNI) : null;
  const zbyvaDni = dobehDo ? rozdilDni(dnes, dobehDo) : null;
  const zbyvaTreninku = Number.parseInt(String(stavTreninku ?? ""), 10);

  return (
    <div className="mt-4 pt-4 border-t border-slate-200">
      <div className="flex items-center gap-2 mb-3">
        <CalendarX className="w-4 h-4 text-rose-500" />
        <h4 className="text-sm font-semibold text-slate-800">{ukonceno ? "Doběh tréninků po ukončení" : "Plánované ukončení"}</h4>
      </div>

      {ukonceno ? (
        dobehDo && zbyvaDni >= 0 ? (
          <div className="rounded-xl border border-brand-200 bg-brand-50 p-3">
            <div className="flex items-center justify-between gap-3 text-sm font-bold text-slate-800">
              <span>Do {fmtDay(dobehDo)}</span>
              <span>zbývá {zbyvaDni} {zbyvaDni === 1 ? "den" : zbyvaDni >= 2 && zbyvaDni <= 4 ? "dny" : "dní"}</span>
            </div>
            <div className="mt-2 h-2.5 rounded-full bg-slate-200"><div className="h-2.5 rounded-full bg-brand" style={{ width: `${Math.max(0, Math.min(100, ((DOBEH_DNI - zbyvaDni) / DOBEH_DNI) * 100))}%` }} /></div>
            <div className="mt-2 text-[13px] text-slate-600">
              {Number.isFinite(zbyvaTreninku) ? `Zbývá ${zbyvaTreninku} ${zbyvaTreninku === 1 ? "trénink" : zbyvaTreninku >= 2 && zbyvaTreninku <= 4 ? "tréninky" : "tréninků"}. ` : ""}Platí minimum tréninků podle balíčku.
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-500">{dobehDo ? `Doběh skončil ${fmtDay(dobehDo)}.` : "Spolupráce je ukončená, Konec není vyplněný."}</div>
        )
      ) : loading ? (
        <div className="text-sm text-slate-400">Načítám…</div>
      ) : plan && !menit ? (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
          <CalendarX className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-amber-900">Ukončení naplánováno na {fmtDay(plan.datum)}</div>
            <div className="text-[13px] text-amber-800">
              {(() => { const n = rozdilDni(dnes, String(plan.datum).substring(0, 10)); return n > 0 ? `Za ${n} ${n === 1 ? "den" : n >= 2 && n <= 4 ? "dny" : "dní"} se stav sám změní na Ukončeno.` : "Dnes se stav změní na Ukončeno."; })()}
            </div>
            {plan.chyba && (
              <div className="text-xs text-rose-600 mt-1 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Poslední pokus selhal: {plan.chyba}</div>
            )}
          </div>
          {!readOnly && (
            <div className="flex flex-shrink-0 flex-wrap justify-end gap-2">
              <button type="button" onClick={() => { setDatum(String(plan.datum).substring(0, 10)); setMenit(true); }} disabled={busy}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border border-amber-300 bg-white px-3 text-sm font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50">
                Změnit datum
              </button>
              <button type="button" onClick={() => setZrusit(true)} disabled={busy}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border border-amber-300 bg-white px-3 text-sm font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50">
                <Trash2 className="w-4 h-4" /> Zrušit plán
              </button>
            </div>
          )}
        </div>
      ) : readOnly ? (
        <div className="text-sm text-slate-400">Ukončení není naplánované.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
          <label className="block text-sm">
            <div className="text-xs text-slate-500 mb-1">Ukončit k datu</div>
            <input type="date" value={datum} min={zitra} onChange={(e) => setDatum(e.target.value)} disabled={busy} className="field w-full disabled:opacity-50" />
          </label>
          <button type="button" onClick={() => { if (datum && datum >= zitra) { setUkonceni(PRAZDNE_UKONCENI); setPotvrdit(true); } else toast("Vyber datum, nejdřív zítra.", "warning"); }} disabled={busy || !datum}
            className="btn btn-secondary">
            <CalendarX className="w-4 h-4" /> {menit ? "Uložit nové datum" : "Naplánovat ukončení"}
          </button>
          {menit && (
            <button type="button" onClick={() => { setMenit(false); setDatum(""); }} disabled={busy} className="btn btn-ghost sm:col-span-2 justify-self-start">Zpět</button>
          )}
        </div>
      )}

      <ConfirmDialog
        open={potvrdit}
        title="Naplánovat ukončení spolupráce?"
        description={datum ? <><strong>{klientName}</strong>: k {fmtDay(datum)} se stav sám změní na Ukončeno a do Konce se zapíše toto datum. Klientka pak má 90 dní (do {fmtDay(pridejDny(datum, DOBEH_DNI))}) na vychození zbývajících tréninků. Týden předem přijde CSM připomínka kvůli fakturaci ve FAPI.<UkonceniVolby value={ukonceni} onChange={setUkonceni} konec={datum} klientId={klientId || notionPageId} /></> : ""}
        confirmLabel="Naplánovat ukončení"
        variant="danger"
        onConfirm={() => { const ch = chybaUkonceni(ukonceni); if (ch) { toast(ch, "warning"); return; } setPotvrdit(false); naplanovat(); }}
        onCancel={() => setPotvrdit(false)}
      />
      <ConfirmDialog
        open={zrusit}
        title="Zrušit plánované ukončení?"
        description={plan ? `Spolupráce k ${fmtDay(plan.datum)} neskončí a stav zůstane, jak je.` : ""}
        confirmLabel="Zrušit plán"
        variant="warning"
        onConfirm={() => { setZrusit(false); zrusitPlan(); }}
        onCancel={() => setZrusit(false)}
      />
    </div>
  );
}
